import React, { useRef, useState } from "react";

import Editor from "@Components/admin/Editor";
import editorThemes from "@Components/admin/editorThemes";
import useJsonValidator from "@Components/admin/hooks/useJsonValidator";
import PageHelmet from "@Components/shared/PageHelmet";
import { IDictionary, IEmptyObject } from "@Components/shared/types";
import { isPresent, prettifyName } from "@Utils";
import { Button, Col, Divider, Layout, Row, Select, Typography } from "antd";

const { Title } = Typography;
const { Content } = Layout;

/**
 * JSON Validator Page
 * - Allows the user to validate JSON data against a JSON schema
 * - Uses AJV for validation
 */
const JSONValidatorPage: React.FC<IEmptyObject> = () => {
    const [data, setData] = useState<IDictionary>();
    const [schema, setSchema] = useState<IDictionary>();
    const [theme, setTheme] = useState<(typeof editorThemes)[number]>("dracula");

    const editorRef = useRef(null);

    const { paragraphText, isComplete } = useJsonValidator({ data, schema });

    return (
        <article>
            <PageHelmet name="JSON Schema Validator" />
            <section className="json-validator-page" style={{ marginTop: "10px" }}>
                <Row gutter={[16, 8]}>
                    <Content>
                        <Row gutter={4} align="middle" style={{ float: "right" }}>
                            <Col>
                                <Title style={{ margin: 0 }} level={5}>
                                    Theme
                                </Title>
                            </Col>
                            <Col>
                                <Select onSelect={setTheme} defaultValue={theme} style={{ width: "175" }}>
                                    {editorThemes.map((theme) => (
                                        <Select.Option key={theme} value={theme}>
                                            {prettifyName(theme.replace(/-|_/g, " "))}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                        <div style={{ minHeight: "3rem", float: "left" }}>
                            {isPresent(paragraphText) ? <Title level={3}>{paragraphText}</Title> : null}
                            {isComplete ? (
                                <Button
                                    className="neumorphic-subtle"
                                    onClick={() => {
                                        void navigator.clipboard.writeText(JSON.stringify(data));
                                    }}
                                >
                                    Copy JSON
                                </Button>
                            ) : null}
                        </div>
                        <Divider />

                        <Row gutter={16}>
                            <Col span={10}>
                                <div>
                                    <Title level={5}>Schema</Title>
                                    <Editor
                                        mode="code"
                                        theme={theme}
                                        onChange={(newSchema) => {
                                            setSchema(newSchema);
                                        }}
                                        ref={editorRef}
                                    />
                                </div>
                            </Col>
                            <Col span={14}>
                                <div>
                                    <Title level={5}>Data</Title>
                                    <Editor
                                        mode="code"
                                        theme={theme}
                                        onChange={(newData) => {
                                            setData(newData);
                                        }}
                                        ref={editorRef}
                                        schema={schema}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Content>
                </Row>
            </section>
        </article>
    );
};

export default JSONValidatorPage;
